import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { TicketBoard as Board, TicketKarte, TicketSpalte, TicketZeile, Unternehmen } from "@kundeninfo/api-client";
import { useTelefon } from "@kundeninfo/ui";
import { Board as MeBoard, Boardspalte, Chip, Hinweis, Optionen } from "@kundeninfo/ui/me-design";
import type { BoardElement } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../../components/meHuellen.js";
import { TicketZeitleiste } from "./TicketZeitleiste.js";
import type { AblageZiel } from "./TicketZeitleiste.js";
import { TicketKarteAnsicht } from "./TicketKarte.js";
import { SpaltenDialog } from "./SpaltenDialog.js";
import { TicketRaster } from "./TicketRaster.js";
import { TicketFilterleiste, filterAnfrage } from "./TicketFilterleiste.js";
import { heuteBeschriftung, popupFuerTag, tagBeschriftung, zeileFuer } from "./ticketFormat.js";
import { useKartenZiehen, zielAttribute as ablageAttribute } from "./ziehhilfe.js";
import { TicketQuittung } from "./TicketQuittung.js";
import { TicketVerschieben } from "./TicketVerschieben.js";
import type { TicketsClient } from "./ticketsClient.js";
import { gemerkteAnsicht, leseFilter, leseOffen, leseZu, merkeAnsicht, merkeFilter, merkeOffen, merkeZu }
  from "./boardSpeicher.js";
import type { BoardFilter } from "./boardSpeicher.js";
import "./Tickets.css";

/**
 * DAS BOARD (Spec „Board"): Spalten nebeneinander auf `me-board`/`me-boardspalte` (me-design
 * a885596, Entscheid 54: Breite 320–640 px, klebender Kopf mit Name, Zähler und Klappknopf, jede
 * Spalte rollt für sich, am Telefon genau eine Spalte mit der Segmentleiste des Elements), je
 * Spalte die Zeitleiste,
 * Werkzeugleiste mit Unternehmen (115, Ort nach 127 vorläufig), Kunde, Zuständig, „nur
 * verknüpfte", Darstellung je Spalte/Raster (F19) und „+ Aufgabe". Lädt beim Öffnen, bei
 * Fokus-Rückkehr, alle 60 s und nach Mitternacht neu (F20); das Rollfeld bleibt dabei dasselbe
 * Element, damit die Scrollposition stehen bleibt (Hausregel). Filter, Darstellung und Auf-/
 * Zuklappen (der Zeitleiste und der Spalte) merkt sich der Browser (F19, `boardSpeicher.ts`).
 * Passen die Spalten mit 320 px nicht nebeneinander, starten erledigte Spalten ohne Zeitleiste
 * zugeklappt (Entscheid 156, ResizeObserver am Board); ein gemerkter Zustand der Spalte geht vor.
 * Die überfälligen Tickets zählt die Fehlerpille der Spalte (`fehler`, me-design 79d237b, auch
 * zugeklappt sichtbar); `randlos` lässt Zeitleisten-Bänder bis an den Rand laufen, die Abstände
 * der Karten setzt `Tickets.css`; im Slot `menue` nur ⋯, die Dichte der Zeitleiste steht im
 * Spaltendialog (Entscheid 155); Ablageziel „Kopf" ist die Spalte selbst (die Daten-Attribute hängen am Host, die Zeilen darin gehen vor).
 * Ziehen am Griff (Übergang T-2, `ziehhilfe.ts`) und „Verschieben …" (F22) gehen an die Ablage;
 * die Karte steht sofort am Ziel, die Antwort des Servers gleicht ab, 409 lädt neu (Review-
 * Fokus 2). Danach die Quittung mit „Rückgängig" als Gegenbuchung (me-hinweis dauer, F23).
 * Die Filter teilt das Board mit der Liste (`TicketFilterleiste.tsx`, Ruling A4); das Raster
 * (`TicketRaster.tsx`, 118; Übergang T-1 Rest: eigenes Gitter) zeichnet in einem eigenen
 * Rollfeld mit denselben Karten und Ablagezielen.
 */

export { ANSICHT_SCHLUESSEL } from "./boardSpeicher.js";
export type { BoardFilter } from "./boardSpeicher.js";

export { filterAnfrage } from "./TicketFilterleiste.js";

const BERLIN = new Intl.DateTimeFormat("de-DE", { timeZone: "Europe/Berlin", hour: "2-digit",
  minute: "2-digit", second: "2-digit", hourCycle: "h23" });

/** Wanduhr-Rechnung: An Tagen der Zeitumstellung liegt der Timer eine Stunde daneben (Gesamtreview
 *  M11). Feuert er zu früh, bleibt `heute` gleich und der Effekt stellt sich nicht neu — der
 *  60-s-Takt lädt dann binnen einer Minute nach Mitternacht nach, die Zeitleiste rückt trotzdem. */
export function msBisMitternachtBerlin(jetzt: Date): number {
  const t = BERLIN.formatToParts(jetzt);
  const w = (typ: string) => Number(t.find((x) => x.type === typ)?.value ?? "0");
  return ((24 - w("hour")) * 3600 - w("minute") * 60 - w("second")) * 1000 - jetzt.getMilliseconds();
}

export function quittungsText(t: TicketKarte, spaltenName: string): string {
  if (t.resubmitDate === null) return `${t.ticketNumber} → ${spaltenName}`;
  const zeit = t.resubmitTime === null ? "" : ` ${t.resubmitTime}`;
  return `${t.ticketNumber} → ${spaltenName}, Wiedervorlage ${tagBeschriftung(t.resubmitDate)}${zeit}`;
}

/** Die Zeile, auf die ein Ziel zeigt (−1: keine Zeile, etwa ein Datum jenseits der 30 Tage). */
function zielZeile(z: AblageZiel, zeilen: readonly TicketZeile[]): number {
  if (z.ziel === "JETZT") return 0;
  const zeile = z.ziel === "TAG" ? zeilen.find((x) => x.art === "TAG" && x.datum === z.datum)
    : zeilen.find((x) => x.art === z.ziel && x.von === z.datum);
  return zeile?.index ?? -1;
}

/** Die gesetzten Filter als kurze Texte für die eingeklappte Filterzeile am Telefon. */
function aktiveFilter(f: BoardFilter, p: Pick<TicketBoardProps, "companies" | "ich">,
                      tickets: readonly TicketKarte[]): string[] {
  const texte: string[] = [];
  if (f.company !== "ALLE") texte.push(p.companies.find((c) => c.id === f.company)?.shortCode ?? "Unternehmen");
  if (f.customer !== "") {
    texte.push(tickets.flatMap((t) => t.customers).find((c) => c.id === f.customer)?.name ?? "gewählter Kunde");
  }
  if (f.assignee !== "") {
    texte.push(f.assignee === "none" ? "Niemand" : f.assignee === p.ich.id ? "Ich"
      : tickets.find((t) => t.assignee?.id === f.assignee)?.assignee?.name ?? "gewählte Person");
  }
  if (f.linked) texte.push("nur verknüpfte");
  return texte;
}

/** Ablageziel „Kopf" am Host der Spalte: Die Hülle reicht keine `data-*` durch, also setzt der
 *  Ref sie (veraltete zuerst weg). Ein Zug über Kopf oder freie Fläche der Spalte trifft den Host
 *  (der Schattenbaum meldet ihn als Ziel); die Zeilen der Zeitleiste darin gehen vor. */
function ablageAmHost(el: HTMLElement, attribute: Record<string, string>): void {
  for (const a of [...el.attributes]) {
    if (a.name.startsWith("data-ablage") && !(a.name in attribute)) el.removeAttribute(a.name);
  }
  for (const [name, wert] of Object.entries(attribute)) {
    if (el.getAttribute(name) !== wert) el.setAttribute(name, wert);
  }
}

/** Mindestbreite der Spalten (Entscheid 54). */
const SPALTE_MIN = 320;

/** Lücke zwischen den Spalten und Innenabstand der Bahn (je Seite) in px. */
export type BahnMasse = { luecke: number; rand: number };
/** Rückfall, wenn das Element `bahnMasse` nicht kennt (wie me-board selbst: 12/4). */
const BAHN_VORGABE: BahnMasse = { luecke: 12, rand: 4 };

/** Liest `board.bahnMasse` (me-design a9a3e5a, aus dem gerechneten Stil der Bahn); fehlt die
 *  Eigenschaft oder ist sie unbrauchbar, gilt die Vorgabe 12/4. */
function bahnMasseVon(el: BoardElement): BahnMasse {
  const m = (el as BoardElement & { bahnMasse?: Partial<BahnMasse> }).bahnMasse;
  const zahl = (n: unknown, vorgabe: number) => (typeof n === "number" && Number.isFinite(n) ? n : vorgabe);
  return { luecke: zahl(m?.luecke, BAHN_VORGABE.luecke), rand: zahl(m?.rand, BAHN_VORGABE.rand) };
}

/**
 * Entscheid 156: Passen die Spalten mit ihrer Mindestbreite nicht nebeneinander, starten die
 * erledigten Spalten ohne Zeitleiste zugeklappt. Eine Breite von 0 heißt „noch nicht gemessen“
 * (nicht gelayoutet) — dann klappt nichts von selbst. Lücke und Rand der Bahn liefert me-board.
 */
export function spaltenZuEng(anzahl: number, breite: number, bahn: BahnMasse = BAHN_VORGABE): boolean {
  if (breite <= 0 || anzahl === 0) return false;
  return anzahl * SPALTE_MIN + (anzahl - 1) * bahn.luecke + 2 * bahn.rand > breite;
}

const startetZu = (s: TicketSpalte) => s.stateKind === "DONE" && s.timelineMode === "NONE";

type Quittung = { text: string; ticketId: string; version: number;
                  vorher: { columnId: string; resubmitDate: string | null; resubmitTime?: string | null } };

const GEAENDERT = "Das Ticket wurde inzwischen geändert.";

export type TicketBoardProps = {
  client: TicketsClient; companies: readonly Unternehmen[]; ich: { id: string; name: string };
  onOeffnen: (id: string) => void; onAnlegen: (companyId: string | null) => void; neuLaden?: number;
};

export function TicketBoard(p: TicketBoardProps): ReactElement {
  const [board, setBoard] = useState<Board | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [filter, setFilter] = useState<BoardFilter>(() => leseFilter(p.companies.map((c) => c.id)));
  const [ansicht, setAnsicht] = useState<"spalten" | "raster">(gemerkteAnsicht);
  const telefon = useTelefon();
  const [telefonSpalte, setTelefonSpalte] = useState<string | null>(null);
  // F36: am Telefon kein Raster; die gemerkte Ansicht (`ansicht`) bleibt dabei unberührt.
  const wirksameAnsicht = telefon ? "spalten" : ansicht;
  // Fehlt die gewählte Spalte (gelöscht, neue Id), gilt die Eingangsspalte, sonst die erste.
  const aktiveTelefonSpalte = board === null ? "" : (board.spalten.find((s) => s.id === telefonSpalte)
    ?? board.spalten.find((s) => s.isInbox) ?? board.spalten[0])?.id ?? "";
  const [offen, setOffen] = useState<Record<string, boolean>>(leseOffen);
  const [zu, setZu] = useState<Record<string, boolean>>(leseZu);
  const [spaltenDialog, setSpaltenDialog] = useState<{ spalte: TicketSpalte | null } | null>(null);
  const boardRef = useRef<BoardElement | null>(null);
  // Breite des Boards (ResizeObserver) für Entscheid 156; 0 = noch nicht gemessen.
  const [boardBreite, setBoardBreite] = useState(0);
  // Lücke und Rand der Bahn, nach dem Einhängen und bei jeder Größenänderung vom Element gelesen.
  const [bahn, setBahn] = useState<BahnMasse>(BAHN_VORGABE);
  const rasterFeld = useRef<HTMLDivElement | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [verschieben, setVerschieben] = useState<TicketKarte | null>(null);
  // Welcher „Verschieben …“-Dialog gerade offen ist — für eine Antwort, die erst nach dem
  // Schließen eintrifft (ihr Befund gehört dann ans Board, nicht an ein Feld, das es nicht mehr gibt).
  const verschiebenOffen = useRef<string | null>(null);
  useEffect(() => { verschiebenOffen.current = verschieben?.id ?? null; }, [verschieben]);
  const [quittung, setQuittung] = useState<Quittung | null>(null);
  // Am Telefon klappen die Filter ein (Sichtprobe 5): eine Zeile „Filter“ mit den aktiven Werten.
  const [filterOffen, setFilterOffen] = useState(false);
  // Während eines Zugs lädt das Board nicht im Takt oder bei Fokus neu (wie `useZiehOrdnung`
  // es seinen Aufrufern anbietet): Die Zielzone soll unter dem Zeiger nicht wechseln.
  const ziehtRef = useRef(false);

  // Nur die jüngste Anfrage zeichnet: Ein schneller Filterwechsel darf nicht von der Antwort
  // auf den alten Filter überholt werden.
  const lauf = useRef(0);
  const laden = useCallback(async () => {
    const nummer = ++lauf.current;
    try {
      const neu = await p.client.getTicketBoard(filterAnfrage(filter));
      if (nummer !== lauf.current) return;
      setBoard(neu);
      setFehler(null);
    } catch {
      if (nummer === lauf.current) setFehler("Das Board konnte nicht geladen werden.");
    }
  }, [p.client, filter]);

  useEffect(() => { void laden(); }, [laden, p.neuLaden]);
  const ladenRef = useRef(laden);
  ladenRef.current = laden;

  useEffect(() => {
    const takt = setInterval(() => { if (!ziehtRef.current) void laden(); }, 60_000);
    const fokus = () => { if (!ziehtRef.current) void laden(); };
    window.addEventListener("focus", fokus);
    return () => { clearInterval(takt); window.removeEventListener("focus", fokus); };
  }, [laden]);

  useEffect(() => {
    if (board === null) return undefined;
    const t = setTimeout(() => { void laden(); }, msBisMitternachtBerlin(new Date()) + 1000);
    return () => clearTimeout(t);
  }, [board?.heute, laden]);

  // Entscheid 156: Die Breite des Boards entscheidet, ob erledigte Spalten zugeklappt starten;
  // bei jeder Größenänderung neu. Am Telefon zeigt das Board ohnehin nur eine Spalte.
  const misstBoard = board !== null && wirksameAnsicht === "spalten" && !telefon;
  useEffect(() => {
    const el = boardRef.current;
    if (!misstBoard || el === null) return undefined;
    const messen = () => {
      setBoardBreite(el.clientWidth);
      const neu = bahnMasseVon(el);
      setBahn((alt) => (alt.luecke === neu.luecke && alt.rand === neu.rand ? alt : neu));
    };
    messen();
    const beobachter = new ResizeObserver(messen);
    beobachter.observe(el);
    return () => beobachter.disconnect();
  }, [misstBoard]);
  const zuEng = !telefon && spaltenZuEng(board?.spalten.length ?? 0, boardBreite, bahn);

  const filterSetzen = (f: Partial<BoardFilter>) => {
    const neu = { ...filter, ...f };
    setFilter(neu);
    merkeFilter(neu);
  };
  const ansichtSetzen = (a: "spalten" | "raster") => {
    setAnsicht(a);
    merkeAnsicht(a);
  };
  const offenSetzen = (id: string, wert: boolean) => {
    setOffen((alt) => {
      const neu = { ...alt, [id]: wert };
      merkeOffen(neu);
      return neu;
    });
  };
  const zuSetzen = (id: string, wert: boolean) => {
    setZu((alt) => {
      const neu = { ...alt, [id]: wert };
      merkeZu(neu);
      return neu;
    });
  };
  /** Legt ab; ist der Dialog „Verschieben …“ beim Eintreffen der Antwort noch offen (`imDialog`
   *  fragt das ab), kommt ein 422-Befund am Feld `uhrzeit` an ihn zurück (Feldfehler statt Hinweis
   *  über dem Board). Ist er inzwischen zu, steht der Befund wie jeder Ablagefehler am Board. */
  async function ablegen(ticketId: string, ziel: AblageZiel, imDialog: () => boolean = () => false):
      Promise<string | null> {
    const t = board?.tickets.find((x) => x.id === ticketId);
    if (board === null || t === undefined) return null;
    // Am eigenen Platz abgelegt (eigene Spalte und Kopf, eigene Zeile oder derselbe Tag): keine
    // Anfrage, keine Quittung — auch nicht für Jetzt auf einem überfälligen oder undatierten Ticket.
    if (ziel.columnId === t.columnId && (ziel.ziel === "KOPF"
        || (ziel.ziel === "TAG" && ziel.datum === t.resubmitDate)
        || zielZeile(ziel, board.zeilen) === zeileFuer(t.resubmitDate, board.zeilen, board.heute))
        // Eine geänderte Uhrzeit am eigenen Platz ist trotzdem eine Ablage.
        && ziel.uhrzeit === undefined) return null;
    // Hat „Verschieben …“ die Uhrzeit geändert, stellt „Rückgängig“ auch sie zurück.
    const vorher = { columnId: t.columnId, resubmitDate: t.resubmitDate,
                     ...(ziel.uhrzeit !== undefined ? { resubmitTime: t.resubmitTime } : {}) };
    // Vorschau bis zur Antwort: Tag und Jetzt zeichnet die Karte sofort am Ziel; das verbindliche
    // Datum (Berliner Tag, Werktag bei Monat/später) setzt der Server (F13, F18).
    setBoard({ ...board, tickets: board.tickets.map((x) => (x.id !== ticketId ? x : {
      ...x, columnId: ziel.columnId,
      ...(ziel.ziel === "TAG" ? { resubmitDate: ziel.datum } : ziel.ziel === "JETZT" ? { resubmitDate: board.heute } : {}),
    })) });
    // Ein Neuladen, das vor dem Ende der Ablage startete (Takt, Fokus), brächte den alten Stand
    // samt alter Version zurück (Gesamtreview M3): Der Zähler verwirft es zu Beginn und am Ende;
    // lief eins dazwischen, lädt das Board danach frisch.
    const stand = ++lauf.current;
    try {
      const neu = await p.client.ablageTicket(ticketId,
        { columnId: ziel.columnId, ziel: ziel.ziel, datum: ziel.datum, version: t.version,
          ...(ziel.uhrzeit !== undefined ? { uhrzeit: ziel.uhrzeit } : {}) });
      const dazwischen = lauf.current !== stand;
      lauf.current += 1;
      if (dazwischen) void ladenRef.current();
      setBoard((b) => (b === null ? b : { ...b, tickets: b.tickets.map((x) => (x.id === ticketId ? neu : x)) }));
      const spalte = board.spalten.find((s) => s.id === neu.columnId)?.name ?? "";
      setQuittung({ text: quittungsText(neu, spalte), ticketId, version: neu.version, vorher });
      setHinweis(null);
      return null;
    } catch (e) {
      await laden();
      const amFeld = e instanceof ApiError && e.status === 422 && imDialog()
        ? e.fields?.find((f) => f.field === "uhrzeit")?.message : undefined;
      if (amFeld !== undefined) return amFeld;
      setHinweis(e instanceof ApiError && e.status === 409 ? GEAENDERT
        : e instanceof ApiError ? (e.fields?.[0]?.message ?? e.message) : "Das Ticket ließ sich nicht verschieben.");
      return null;
    }
  }

  /** Rückgängig (F23): Gegenbuchung per PATCH mit der NEUEN Version auf Spalte und Datum von vorher. */
  async function rueckgaengig(q: Quittung): Promise<void> {
    setQuittung(null);
    try {
      await p.client.updateTicket(q.ticketId, q.version, q.vorher);
      setHinweis(null);
    } catch (e) {
      setHinweis(e instanceof ApiError && e.status === 409 ? GEAENDERT : "Rückgängig ist nicht gelungen.");
    }
    await laden();
  }

  // Rand-Rollen (T-2): im Raster dessen Rollfeld; im Board die waagerechte Bahn des Boards und die
  // Spalte unter dem Zeiger — beide rollen im Schattenbaum der Elemente (Teile `bahn`/`spalte`).
  const rollfelder = useCallback((x: number, y: number): HTMLElement[] => {
    if (rasterFeld.current !== null) return [rasterFeld.current];
    const felder: HTMLElement[] = [];
    const bahn = boardRef.current?.shadowRoot?.querySelector<HTMLElement>('[part~="bahn"]');
    if (bahn !== null && bahn !== undefined) felder.push(bahn);
    const unter = typeof document.elementFromPoint === "function" ? document.elementFromPoint(x, y) : null;
    const spalte = unter?.closest("me-boardspalte")?.shadowRoot?.querySelector<HTMLElement>('[part~="spalte"]');
    if (spalte !== null && spalte !== undefined) felder.push(spalte);
    return felder;
  }, []);
  const zieh = useKartenZiehen({ rollfelder, onAblage: (id, z) => { void ablegen(id, z); } });
  ziehtRef.current = zieh.zug !== null;

  const karte = (t: TicketKarte) => (
    <TicketKarteAnsicht key={t.id} t={t} heute={board!.heute} ichId={p.ich.id} companies={p.companies}
      onOeffnen={p.onOeffnen} onVerschieben={setVerschieben}
      griff={zieh.griff(t.id)} zieht={zieh.zug?.ticketId === t.id} />
  );
  // Ein Tag am Wochenende oder Feiertag bleibt Ziel, wird aber als Warnung getönt (119).
  const zielAttribute = (z: AblageZiel): Record<string, string> =>
    ablageAttribute(z, z.ziel === "TAG" && z.datum !== null ? popupFuerTag(z.datum, board?.kalender ?? []) : "");

  const offeneAnzahl = (board?.tickets ?? []).filter((t) => t.stateKind !== "DONE").length;

  return (
    <section className="tb" aria-label="Tickets">
      <div className="tb__kopf">
        <h2 className="tb__pfad">Tickets › Board</h2>
        <span className="tb__offen">{offeneAnzahl} offen</span>
        {board !== null && <span className="tb__heute">Heute {heuteBeschriftung(board.heute)}</span>}
      </div>
      <div className="tb__werkzeuge" role="toolbar" aria-label="Werkzeuge des Boards">
        {telefon && (
          <div className="tb__filterzeile">
            <KiKnopf art="leise" ausgeklappt={filterOffen} onClick={() => setFilterOffen(!filterOffen)}>
              Filter
            </KiKnopf>
            <span className="tb__aktivefilter">
              {aktiveFilter(filter, p, board?.tickets ?? []).map((text) => <Chip key={text}>{text}</Chip>)}
            </span>
            <KiKnopf art="primaer" onClick={() => p.onAnlegen(filter.company === "ALLE" ? null : filter.company)}>
              + Aufgabe
            </KiKnopf>
          </div>
        )}
        {/* Eingeklappt bleiben die Filter im Baum (versteckt): Der gewählte Kunde behält seinen Namen. */}
        <div className="tb__filter" hidden={telefon && !filterOffen}>
          <TicketFilterleiste client={p.client} companies={p.companies} ich={p.ich} tickets={board?.tickets ?? []}
            filter={filter} onFilter={filterSetzen} />
        </div>
        {!telefon && (
          <Optionen beschriftung="Darstellung" darstellung="segmente" richtung="waagerecht" wert={ansicht}
            onGeaendert={(d) => ansichtSetzen(d.wert === "raster" ? "raster" : "spalten")}>
            <option value="spalten">je Spalte</option>
            <option value="raster">Raster</option>
          </Optionen>
        )}
        {/* Spaltenpflege nur ab Tablet (Task 18): „Neue Spalte“ in der Werkzeugleiste — me-board
            nimmt nur Spalten als Kinder. */}
        {!telefon && (
          <KiKnopf art="leise" onClick={() => setSpaltenDialog({ spalte: null })}>Neue Spalte</KiKnopf>
        )}
        {!telefon && (
          <KiKnopf art="primaer" onClick={() => p.onAnlegen(filter.company === "ALLE" ? null : filter.company)}>
            + Aufgabe
          </KiKnopf>
        )}
      </div>
      {/* Die Quittung schwebt unten über dem Inhalt, steht im Baum aber gleich nach der
          Werkzeugleiste: Der Tabweg erreicht „Rückgängig" vor allen Karten. Der Schlüssel macht
          jede Ablage zu einer frischen Quittung (Uhr neu, auch bei gleichem Text). */}
      {quittung !== null && (
        <TicketQuittung key={`${quittung.ticketId}:${quittung.version}`} text={quittung.text}
          onSchliessen={() => setQuittung(null)}
          onRueckgaengig={() => { void rueckgaengig(quittung); }} />
      )}
      {fehler !== null && <Hinweis art="fehler">{fehler}</Hinweis>}
      {hinweis !== null && <Hinweis art="warnung">{hinweis}</Hinweis>}
      {board !== null && wirksameAnsicht === "raster" && (
        <div className="tb__raster" ref={rasterFeld}>
          <TicketRaster board={board} karte={karte} zielAttribute={zielAttribute} />
        </div>
      )}
      {board !== null && wirksameAnsicht === "spalten" && (
        // Am Telefon zeigt das Element genau eine Spalte und darüber seine Segmentleiste (Name und
        // Zähler); `aktiv` hält die gewählte Spalte, fehlt sie, gilt der Eingang.
        <MeBoard ref={boardRef} className="tb__board" aria-label="Spalten" spaltenbreiteMin={320}
          spaltenbreiteMax={640} aktiv={aktiveTelefonSpalte} onSpalteGewaehlt={(d) => setTelefonSpalte(d.id)}>
          {board.spalten.map((s) => {
            const tickets = board.tickets.filter((t) => t.columnId === s.id);
            const ueberfaellig = tickets.filter((t) => t.resubmitDate !== null && t.resubmitDate < board.heute
                                                     && s.timelineMode !== "NONE").length;
            const istOffen = offen[s.id] ?? s.timelineMode === "EXPANDED";
            const kopfZiel = zielAttribute({ columnId: s.id, ziel: "KOPF", datum: null, text: s.name });
            return (
              <Boardspalte key={s.id} id={s.id} name={s.name} zaehler={tickets.length}
                fehler={ueberfaellig} fehlerText="überfällig" randlos
                zugeklappt={zu[s.id] ?? (zuEng && startetZu(s))} onUmgeschaltet={(d) => zuSetzen(s.id, d.zugeklappt)}
                ref={(el) => { if (el !== null) ablageAmHost(el, kopfZiel); }}
                menue={(
                  <>
                    <KiKnopf art="leise" name={`Spalte ${s.name} bearbeiten`}
                      onClick={() => setSpaltenDialog({ spalte: s })}>⋯</KiKnopf>
                  </>
                )}>
                <TicketZeitleiste spalte={s} zeilen={board.zeilen} heute={board.heute} tickets={tickets}
                  kalender={board.kalender} offen={istOffen} karte={karte} zielAttribute={zielAttribute} />
              </Boardspalte>
            );
          })}
        </MeBoard>
      )}
      {zieh.zug !== null && (
        // Nur fürs Auge (der Griff ist für die Vorlesestimme stumm); angesagt wird das Ergebnis
        // einmal, in der Quittung.
        <div className="tb__vorschau" aria-hidden="true">{zieh.zug.ziel?.text ?? "Ziel wählen …"}</div>
      )}
      {verschieben !== null && board !== null && (
        <TicketVerschieben t={verschieben} spalten={board.spalten} zeilen={board.zeilen} heute={board.heute}
          kalender={board.kalender} onAbbrechen={() => setVerschieben(null)}
          onAblage={async (z) => {
            // Der Dialog bleibt offen, bis die Ablage steht: Ein Befund an der Uhrzeit gehört an
            // ihr Feld (F93); alles andere meldet das Board wie beim Ziehen.
            const id = verschieben.id;
            const fehler = await ablegen(id, z, () => verschiebenOffen.current === id);
            if (fehler === null) setVerschieben(null);
            return fehler;
          }} />
      )}
      {spaltenDialog !== null && board !== null && (
        // Die Spalte aus dem frischen Stand (Version und Lage nach einem Neuladen); der
        // Dialog selbst bleibt beim Neuladen stehen (Hausregel), seine Eingaben auch.
        <SpaltenDialog client={p.client} spalten={board.spalten}
          spalte={spaltenDialog.spalte === null ? null
            : board.spalten.find((s) => s.id === spaltenDialog.spalte!.id) ?? spaltenDialog.spalte}
          dichteOffen={spaltenDialog.spalte === null ? undefined
            : offen[spaltenDialog.spalte.id] ?? spaltenDialog.spalte.timelineMode === "EXPANDED"}
          onDichte={(wert) => { if (spaltenDialog.spalte !== null) offenSetzen(spaltenDialog.spalte.id, wert); }}
          onAbbrechen={() => setSpaltenDialog(null)} onNeuLaden={laden}
          onFertig={() => { setSpaltenDialog(null); void laden(); }} />
      )}
    </section>
  );
}
